import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiEdit3, FiTrash2, FiClock, FiFlag } = FiIcons;

const TaskItem = ({ task, onToggle, onDelete, onEdit }) => {
  const priorityColors = {
    high: 'text-red-600 bg-red-50',
    medium: 'text-yellow-600 bg-yellow-50',
    low: 'text-green-600 bg-green-50'
  };

  const categoryColors = {
    work: 'text-blue-600 bg-blue-50',
    personal: 'text-purple-600 bg-purple-50',
    health: 'text-green-600 bg-green-50',
    education: 'text-indigo-600 bg-indigo-50',
    finance: 'text-orange-600 bg-orange-50'
  };

  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && !task.completed;

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className={`p-4 rounded-xl border transition-all duration-200 ${
        task.completed 
          ? 'bg-gray-50 border-gray-200 opacity-75' 
          : isOverdue 
            ? 'bg-red-50 border-red-200' 
            : 'bg-white border-gray-200 hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-4">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onToggle(task.id)}
          className={`mt-1 p-2 rounded-lg transition-colors ${
            task.completed
              ? 'bg-green-500 text-white'
              : 'bg-gray-100 text-gray-400 hover:bg-green-500 hover:text-white'
          }`}
        >
          <SafeIcon icon={FiCheck} className="text-sm" />
        </motion.button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-2">
            <h3 className={`font-semibold text-lg ${
              task.completed ? 'text-gray-500 line-through' : 'text-gray-800'
            }`}>
              {task.title}
            </h3>
            
            <div className="flex items-center gap-2 shrink-0">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => onEdit(task)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiEdit3} className="text-sm" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => onDelete(task.id)}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </motion.button>
            </div>
          </div>

          {task.description && (
            <p className={`text-sm mb-3 ${
              task.completed ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {task.description}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${priorityColors[task.priority]}`}>
              <SafeIcon icon={FiFlag} className="text-xs" />
              {task.priority} priority
            </span>

            <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[task.category]}`}>
              {task.category}
            </span>

            {task.dueDate && (
              <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
                isOverdue 
                  ? 'text-red-600 bg-red-100' 
                  : 'text-gray-600 bg-gray-100'
              }`}>
                <SafeIcon icon={FiClock} className="text-xs" />
                Due {format(new Date(task.dueDate), 'MMM dd, yyyy')}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TaskItem;